import useTransactionsApi from '../api/useTransactionsApi';
import { useApiErrorSnackbar } from '../common/useApiErrorSnackbar';
import {
  type SendPaymentBodyType,
  type SimulateMoneyInBodyType,
  type SendPaymentOptionsType,
  type StoreLoadOptions,
  type WalletApiResponse
} from '@/types';
import useWalletsApi from '~/composables/api/useWalletsApi';
import {
  formatSendPaymentBody as buildSendPaymentBody,
  formatRefundPaymentBody as buildRefundPaymentBody
} from '~/utils/transactions/transaction-payload';

// Each call creates its own form refs, matching the previous per-consumer behaviour of useTransactions.
export const useTransactionForms = () => {
  const { $i18n } = useNuxtApp();
  const { t, locale } = $i18n;
  const route = useRoute();
  const { getInOutTransactionDetails } = useTransactionsApi();
  const { getWalletByExtId } = useWalletsApi();
  const { getDisplayExpr, convertToSearchParams } = useUtils();
  const { notifyApiError } = useApiErrorSnackbar();

  const transactionId = computed(() =>
    Array.isArray(route.params.id) ? route.params.id[0] : route.params.id
  );

  const initialPaymentBody = ref<SendPaymentBodyType>({
    DebitWallet: { value: [], isValid: undefined },
    CreditWallet: { value: [], isValid: undefined },
    Amount: { value: '', isValid: undefined },
    Message: { value: '', isValid: undefined },
    Password: { value: '', isValid: undefined },
    originTransaction: { value: [], isValid: undefined },
    language: locale.value
  });

  const initialSimulateMoneyInBody = ref<SimulateMoneyInBodyType>({
    receiver: { value: [], isValid: undefined },
    Amount: { value: '', isValid: undefined },
    comment: { value: '', isValid: undefined },
    Password: { value: '', isValid: undefined },
    TransferId: { value: '', isValid: undefined },
    language: locale.value
  });

  const sendPaymentBody = ref<SendPaymentBodyType>({
    ...initialPaymentBody.value
  });

  const currency = ref(localStorage.getItem('partnerCurrency') || 'EUR');
  const options = ref<SendPaymentOptionsType>({
    DebitWallet: [],
    CreditWallet: [],
    originTransaction: []
  });
  const showCredit = ref(true);
  const selectedPaymentMethod = ref(['bank_transfer']);

  const loadWalletByExtId = async (extId: string): Promise<any> => {
    try {
      const queryObject: StoreLoadOptions = {
        skip: 0,
        take: 10,
        sort: JSON.stringify([{ selector: 'intId', desc: true }]),
        filter: JSON.stringify(['wallet', '=', extId])
      };

      const queryParams = convertToSearchParams(queryObject);
      const response: WalletApiResponse = await getWalletByExtId(queryParams.toString());

      if (response && Array.isArray(response.data) && response.data.length > 0) {
        return response.data[0];
      }

      return null;
    } catch (error: unknown) {
      notifyApiError(error, { title: t('common.transactionDetailsNotFound') });
    }
  };

  const handleLoadDebitAccount = async (walletExtId: Array<string>) => {
    try {
      if (walletExtId[0].localeCompare('sc', undefined, { sensitivity: 'base' }) === 0) {
        options.value.DebitWallet = [
          { label: t('siteMap.myWallet'), value: walletExtId[0], icon: 'Storefront' }
        ];
      } else if (walletExtId[0].localeCompare('lw', undefined, { sensitivity: 'base' }) === 0) {
        options.value.DebitWallet = [
          { label: 'Lemonway (LW)', value: walletExtId[0], icon: 'Lemonway' }
        ];
      } else {
        const { wallet, clientFirstName, clientLastName, companyName, isCompany } =
          await loadWalletByExtId(walletExtId[0]);
        const walletName = isCompany ? companyName : `${clientFirstName} ${clientLastName}`;
        options.value.DebitWallet = [
          {
            label: `${walletName} (${wallet})`,
            subtitles: [],
            value: walletExtId[0],
            icon: isCompany ? 'MyAccount' : 'Person'
          }
        ];
      }
      initialPaymentBody.value.DebitWallet.isValid = true;
    } catch {
      initialPaymentBody.value.DebitWallet.isValid = false;
      initialPaymentBody.value.DebitWallet.value = [];
    }
  };

  const handleLoadCreditAccount = async (walletExtId: Array<string>) => {
    try {
      if (walletExtId[0].localeCompare('sc', undefined, { sensitivity: 'base' }) === 0) {
        options.value.CreditWallet = [
          { label: t('siteMap.myWallet'), value: walletExtId[0], icon: 'Storefront' }
        ];
      } else if (walletExtId[0].localeCompare('lw', undefined, { sensitivity: 'base' }) === 0) {
        options.value.CreditWallet = [
          { label: 'Lemonway (LW)', value: walletExtId[0], icon: 'Lemonway' }
        ];
      } else {
        const { wallet, clientFirstName, clientLastName, companyName, isCompany } =
          await loadWalletByExtId(walletExtId[0]);
        const walletName = isCompany ? companyName : `${clientFirstName} ${clientLastName}`;
        options.value.CreditWallet = [
          {
            label: `${walletName} (${wallet})`,
            subtitles: [],
            value: walletExtId[0],
            icon: isCompany ? 'MyAccount' : 'Person'
          }
        ];
      }
      initialPaymentBody.value.CreditWallet.isValid = true;
    } catch {
      initialPaymentBody.value.CreditWallet.isValid = false;
      initialPaymentBody.value.CreditWallet.value = [];
    }
  };

  const handleLoadTransaction = async (transactionId?: string) => {
    if (transactionId) {
      try {
        const data = await getInOutTransactionDetails(transactionId);
        const { MoneyTransaction } = data;
        options.value.originTransaction = [
          {
            value: MoneyTransaction.id.toString(),
            label: `Id: ${transactionId} (${getDisplayExpr('transactionTypes', MoneyTransaction.type)} - ${getDisplayExpr('paymentMethods', MoneyTransaction.paymentMethod)} - €${MoneyTransaction.amount.toFixed(2)})`,
            icon: 'Transactions',
            subtitles: []
          }
        ];
        initialPaymentBody.value.originTransaction.isValid = true;
      } catch {
        initialPaymentBody.value.originTransaction.isValid = false;
        initialPaymentBody.value.originTransaction.value = [];
      }
    }
  };

  const formatSendPaymentBody = () => buildSendPaymentBody(initialPaymentBody.value);
  const formatRefundPaymentBody = () =>
    buildRefundPaymentBody(initialPaymentBody.value, transactionId.value);

  return {
    initialPaymentBody,
    initialSimulateMoneyInBody,
    sendPaymentBody,
    currency,
    options,
    showCredit,
    selectedPaymentMethod,
    loadWalletByExtId,
    handleLoadDebitAccount,
    handleLoadCreditAccount,
    handleLoadTransaction,
    formatSendPaymentBody,
    formatRefundPaymentBody
  };
};
